import type { ReactNode } from 'react';
import { LAYOUT_LIMITS } from '@jerome2/shared';
import { listDate } from '../lib/format.ts';
import { useStore } from '../lib/store.ts';
import { chatStore, sortedChats } from '../state/chat.ts';
import { tabs } from '../state/tabs.ts';
import { Icon } from '../ui/Icon.tsx';
import { Button, UnreadDot } from '../ui/primitives.tsx';

// Gesprekkenlijst: Nieuw gesprek, Actief/Archief en de gesprekken (vastgezet eerst, dan recent), elk met de titel over hooguit
// twee regels en de datum rechts bovenaan; de stand van het model en de kosten staan in het gesprek zelf en in Gebruik (ticket
// 28079). De titel komt na een geslaagde beurt uit het model (server/src/chat/title.ts, hooguit 48 tekens) en komt live binnen
// (chat.updated); op 280 px past dat niet op één regel, dus loopt hij door in plaats van af te kappen.
// Op de telefoon vult hij het scherm, met `top` (de briefing van vandaag) onder de kop en Actief | Archief
// als segmentknoppen van 44 px.
// Op de desktop klapt de lijst met de knop in de kop (`onCollapse`) in tot de strook `ChatStrip` van 44 px en weer uit. Daar staat
// hij als kolom in de schil links van de tabbalk (shell/ChatColumn.tsx), met de ingestelde breedte (`width`, standaard
// 280 px, ticket 28376); de kop van lijst en strook is even hoog als de tabbalk.
// Een gesprek met een antwoord dat de eigenaar nog niet zag, heeft een stip rechts bovenaan bij de datum (ticket 30961, deel 2;
// `unread` in de tabstaat, zie state/chatTurns.ts), ook als het geen tabblad heeft; de stip gaat weg zodra het gesprek in beeld is.

/** Een vak zo hoog als één regel van de titel (`1lh`): het teken of de datum erin staat op de eerste regel, ook als de titel over
 * twee regels loopt. */
const FIRST_LINE = 'h-[1lh] shrink-0 flex items-center gap-2';

/** `onCollapse`: de knop in de kop die de lijst tot de strook inklapt (desktop); op de telefoon is die er niet, daar vult de lijst het scherm.
 * `width`: de breedte op de desktop in px (de kolom in de schil); op de telefoon vult de lijst de breedte. */
export function ChatList(props: { chatId: string | null; narrow: boolean; archived: boolean; onArchived: (archived: boolean) => void; onOpen: (id: string) => void; onNew: () => void; onCollapse?: (() => void) | undefined; top?: ReactNode; width?: number | undefined }) {
  const all = useStore(chatStore, (s) => s.chats);
  const loaded = useStore(chatStore, (s) => s.loaded);
  const chats = sortedChats({ chats: all }, 'chat', { archived: props.archived });
  const unread = useStore(tabs, (s) => s.unread);
  return (
    <aside className={`${props.narrow ? 'w-full' : 'shrink-0 border-r border-line'} h-full flex flex-col bg-paper`} style={props.narrow ? undefined : { width: props.width ?? LAYOUT_LIMITS.chatColumn.default }} aria-label="Gesprekken">
      <div className={`${props.narrow ? 'h-14' : 'h-11'} shrink-0 flex items-center gap-2 px-3 border-b border-line`}>
        <Button size="sm" variant="primary" icon="plus" onClick={props.onNew}>
          Nieuw gesprek
        </Button>
        {props.narrow ? (
          <Segments archived={props.archived} onArchived={props.onArchived} />
        ) : (
          <button type="button" className={`ml-auto text-[13px] ${props.archived ? 'text-ink' : 'text-muted hover:text-ink'}`} onClick={() => props.onArchived(!props.archived)}>
            {props.archived ? 'Actief' : 'Archief'}
          </button>
        )}
        {props.onCollapse ? (
          <button type="button" aria-label="Gesprekken inklappen" title="Gesprekken inklappen" onClick={props.onCollapse} className="h-7 w-7 shrink-0 inline-flex items-center justify-center rounded-md text-ink-soft hover:bg-tint hover:text-ink">
            <Icon name="chevron" size={16} className="rotate-180" />
          </button>
        ) : null}
      </div>
      {props.top}
      <ul className="flex-1 min-h-0 overflow-auto scroll-thin">
        {chats.map((c) => (
          <li key={c.id}>
            <button type="button" onClick={() => props.onOpen(c.id)} className={`w-full text-left px-3 ${props.narrow ? 'py-3' : 'py-2.5'} border-b border-line ${c.id === props.chatId ? 'bg-tint' : 'hover:bg-tint/50'}`} aria-current={c.id === props.chatId ? 'true' : undefined}>
              <div className="flex items-start gap-2">
                {c.pinned ? (
                  <span className={FIRST_LINE}>
                    <Icon name="star" size={12} className="text-amber" />
                  </span>
                ) : null}
                <span className="font-medium flex-1 min-w-0 line-clamp-2 break-words">{c.title}</span>
                <span className={FIRST_LINE}>
                  {unread?.includes(c.id) ? <UnreadDot /> : null}
                  {c.status === 'running' ? <Icon name="spinner" size={12} className="animate-spin text-accent" /> : null}
                  <span className="text-[12px] text-muted">{listDate(c.lastMessageAt ?? c.createdAt)}</span>
                </span>
              </div>
            </button>
          </li>
        ))}
        {!chats.length && loaded ? <li className="p-4 text-[13px] text-muted">{props.archived ? 'Geen gearchiveerde gesprekken.' : 'Nog geen gesprekken.'}</li> : null}
      </ul>
    </aside>
  );
}

/** De ingeklapte lijst op de desktop: een strook van 44 px met Nieuw gesprek, de uitklapknop en het aantal gesprekken waar Jerome mee bezig is. */
export function ChatStrip(props: { onExpand: () => void; onNew: () => void }) {
  const all = useStore(chatStore, (s) => s.chats);
  const running = sortedChats({ chats: all }, 'chat', { archived: false }).filter((c) => c.status === 'running').length;
  const summary = running ? `${running} bezig` : 'rustig';
  return (
    <aside className="w-11 shrink-0 h-full flex flex-col bg-paper border-r border-line" aria-label="Gesprekken">
      <div className="h-11 shrink-0 flex items-center justify-center border-b border-line">
        <button type="button" aria-label="Nieuw gesprek" title="Nieuw gesprek" onClick={props.onNew} className="h-8 w-8 inline-flex items-center justify-center rounded-md bg-accent text-white hover:bg-accent-deep">
          <Icon name="plus" size={16} />
        </button>
      </div>
      <button type="button" aria-label="Gesprekken uitklappen" title={`Gesprekken uitklappen (${summary})`} onClick={props.onExpand} className="flex-1 flex flex-col items-center gap-2 pt-1.5 pb-3 text-ink-soft hover:bg-tint hover:text-ink">
        <span className="h-8 w-8 inline-flex items-center justify-center rounded-md">
          <Icon name="chevron" size={16} />
        </span>
        {running ? <span data-count="running" className="h-5 min-w-5 px-1 rounded-full bg-accent text-white text-[12px] font-medium inline-flex items-center justify-center">{running}</span> : null}
        <span className="text-[12px] font-medium [writing-mode:vertical-rl]">Gesprekken</span>
      </button>
    </aside>
  );
}

/** Actief | Archief als segmentknoppen van 44 px, zoals Dag | Week | Maand in de agenda. */
function Segments(props: { archived: boolean; onArchived: (archived: boolean) => void }) {
  return (
    <div role="group" aria-label="Gesprekken" className="ml-auto flex items-center rounded-md bg-tint p-0.5 text-[14px]">
      {[false, true].map((archived) => (
        <button key={String(archived)} type="button" aria-pressed={props.archived === archived} className={`h-11 px-4 rounded ${props.archived === archived ? 'bg-surface shadow-sm font-medium' : 'text-muted'}`} onClick={() => props.onArchived(archived)}>
          {archived ? 'Archief' : 'Actief'}
        </button>
      ))}
    </div>
  );
}
